import { useCallback } from 'react'
import useSWRInfinite from 'swr/infinite'
import { z } from 'zod'
import { ESTextbook } from '../../elastic_search'
import { fb } from '../../firebase'
import { TextbooksSearchEndpointParams } from '../../shared/firebase/functions/search_endpoint'
import { deserializeArray } from '../../utils'

type Params = z.input<typeof TextbooksSearchEndpointParams>
type SWRKey = {
  index: 'textbooks'
  params: Params
  page: number
  size: number
}

const PAGE_SIZE = 20

const fetchTextbooks = async (key: SWRKey): Promise<ESTextbook[]> => {
  const { data } = await fb.call('search')(key)
  if (data.index !== 'textbooks') throw new Error('Unexpected result.')
  return deserializeArray(data.result, {})
}

export const useESTextbooks = (
  params: Params | null,
  fallback?: ESTextbook[]
) => {
  const getKey = useCallback(
    (index: number): SWRKey | null => {
      if (params === null) return null
      return { params, page: index + 1, size: PAGE_SIZE, index: 'textbooks' }
    },
    [params]
  )
  const { data, size, setSize, isLoading, isValidating } = useSWRInfinite<
    ESTextbook[]
  >(getKey, fetchTextbooks, {
    fallbackData: fallback ? [fallback] : undefined,
  })

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty = data?.[0] !== undefined ? data[0].length === 0 : undefined
  const isReachingEnd =
    data !== undefined
      ? data.length >= 2 &&
        data[data.length - 2]?.length < PAGE_SIZE &&
        data[data.length - 1]?.length === 0
      : undefined
  const isRefreshing = isValidating && data && data.length === size

  const textbooks: ESTextbook[] | undefined =
    data && data.length > 0 ? ([] as ESTextbook[]).concat(...data) : undefined

  const loadMore = async () => {
    await setSize(size + 1)
  }

  return {
    textbooks,
    loadMore,
    isReachingEnd,
    isLoadingMore,
    isEmpty,
    isRefreshing,
  }
}
